<template>
  <span class="task-copy-link-button">
    <!--
      Loading skeleton — reserves the slot during the first in-flight fetch so
      the card layout doesn't shift when the button appears. Mirrors
      TaskIsolateButton.vue's skeleton: hand-drawn link-chain SVG, all edges
      sharing a synchronized opacity "breathing" pulse. Only opacity animates,
      so this is essentially free on the GPU compositor.
    -->
    <div v-if="loading && !dataFetched" class="task-copy-link-button__skeleton" aria-hidden="true">
      <svg
        viewBox="0 0 24 24"
        class="task-copy-link-button__skeleton-svg"
        xmlns="http://www.w3.org/2000/svg">
        <g fill="none" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
          <!-- Two interlocked oblongs forming a link-chain glyph. Drawn as
               two stadiums (rounded rectangles) tilted ±20° around the
               center, intersecting where a chain joins. -->
          <path
            d="M9.5 14.5 a3 3 0 0 1 0-4.2 l3-3 a3 3 0 0 1 4.2 4.2 l-1.5 1.5"
            class="task-copy-link-button__skeleton-edge" />
          <path
            d="M14.5 9.5 a3 3 0 0 1 0 4.2 l-3 3 a3 3 0 0 1-4.2-4.2 l1.5-1.5"
            class="task-copy-link-button__skeleton-edge" />
        </g>
      </svg>
    </div>

    <!--
      Real button — rendered once we have data. Always visible (the universal
      rule guarantees at least the current facility is a valid target).
      The inline v-menu activator only binds menuOn/menuAttrs when the
      click behavior is 'navigate-menu' so single-target cases don't trigger
      the menu open on click.
    -->
    <v-tooltip v-else right>
      <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
        <v-menu
          v-model="showFacilityMenu"
          :close-on-content-click="true"
          offset-y
          left
          transition="scale-transition">
          <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
            <v-btn
              :color="buttonColor"
              icon
              small
              v-bind="{
                ...tooltipAttrs,
                ...(clickBehavior === 'navigate-menu' ? menuAttrs : {}),
              }"
              v-on="{
                ...tooltipOn,
                ...(clickBehavior === 'navigate-menu' ? menuOn : {}),
              }"
              :class="{ 'copy-link-btn--cross-facility': buttonColor === 'warning' }"
              @click.stop="handleClick"
              @contextmenu.prevent.stop="handleRightClick">
              <v-icon small>mdi-link-variant</v-icon>
            </v-btn>
          </template>

          <!-- Facility Selection Menu — items are intent-aware (3D / 360 / board) -->
          <v-card class="copy-link-menu" elevation="8">
            <v-list dense class="copy-link-menu__list">
              <v-subheader class="copy-link-menu__header">
                <v-icon small class="mr-2">mdi-link-variant</v-icon>
                Copy link for facility
              </v-subheader>
              <v-divider></v-divider>
              <v-list-item
                v-for="item in menuItems"
                :key="item.facilityId"
                class="copy-link-menu__item"
                @click="copyForFacility(item)">
                <v-list-item-icon class="mr-3">
                  <v-icon small :color="item.intent === 'task-board' ? 'grey' : 'warning'">
                    {{ item.icon }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="copy-link-menu__name">
                    {{ item.facilityName }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="copy-link-menu__intent">
                    {{ intentLabel(item.intent) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </template>
      <span>{{ tooltipText }}</span>
    </v-tooltip>
  </span>
</template>

<script>
  import {
    getTaskIsolationInfo,
    getCopyLinkBehavior,
    getCopyLinkButtonColor,
    getCopyLinkMenuItems,
    getCopyLinkDirectTarget,
    awaitInFlightPrefetch,
  } from '@Task/utils/taskIsolation';
  import { copyTaskLinkToClipboard } from '@/utils/shareIntents';
  import { log as debugLog, error as debugError } from '@/utils/debugLogger';

  export default {
    name: 'TaskCopyLinkButton',

    props: {
      /**
       * Task object — must include id, taskTypeId (or the parent route
       * provides taskTypeId), and forgeTagsIds.
       */
      task: {
        type: Object,
        required: true,
      },
      /**
       * Optional override for taskTypeId. Falls back to the parent route's
       * params.taskTypeId, matching the convention used by Kanban/List/Gantt.
       */
      taskTypeId: {
        type: String,
        default: null,
      },
    },

    data() {
      return {
        loading: false,
        isolationInfo: null,
        dataFetched: false,
        showFacilityMenu: false,
      };
    },

    computed: {
      facilityDict() {
        return this.$store.getters.getFacilityDict || {};
      },
      currentAssetId() {
        return this.$store.state.assetId || null;
      },
      effectiveTaskTypeId() {
        return this.taskTypeId || this.$route?.params?.taskTypeId || null;
      },
      clickBehavior() {
        return getCopyLinkBehavior(this.isolationInfo);
      },
      buttonColor() {
        return getCopyLinkButtonColor(this.isolationInfo);
      },
      menuItems() {
        return getCopyLinkMenuItems(this.isolationInfo, this.currentAssetId, this.facilityDict);
      },
      tooltipText() {
        if (!this.isolationInfo) return 'Copy link to task';
        if (this.clickBehavior === 'navigate-menu') {
          return 'Copy link to a facility (right-click for options)';
        }
        return 'Copy link to task';
      },
    },

    mounted() {
      debugLog('TaskCopyLinkButton', 'mounted, kicking off ensureDataFetched', {
        taskId: this.task?.id,
        tagCount: this.task?.forgeTagsIds?.length ?? 0,
      });
      this.ensureDataFetched();
    },

    methods: {
      /**
       * Fetch isolation info — same pattern as TaskIsolateButton. Idempotent
       * via the loading/dataFetched flags, and waits for any board-level
       * batched prefetch so we don't race-fall-back to the per-task path.
       */
      async ensureDataFetched() {
        if (this.dataFetched || this.loading) return;
        if (!this.task) {
          this.dataFetched = true;
          return;
        }

        this.loading = true;
        try {
          await awaitInFlightPrefetch();
          // Note: getTaskIsolationInfo returns null when there are no
          // forgeTagsIds — that's fine for copy-link because the universal
          // rule still allows direct-copy to current facility. We synthesize
          // a minimal isolationInfo in that case so the helpers downstream
          // see consistent shape.
          let info = await getTaskIsolationInfo(this.task, this.$store);
          if (!info) {
            info = {
              combinedAssets: [],
              otherAssets: [],
              hasCurrentFacility: false,
              harvestedAssets: [],
              positionedAssets: [],
              isHarvestedInCurrentFacility: false,
              isPositionedInCurrentFacility: false,
            };
          }
          this.isolationInfo = info;
          this.dataFetched = true;
          debugLog('TaskCopyLinkButton', 'data fetched', {
            taskId: this.task?.id,
            buttonColor: this.buttonColor,
            clickBehavior: this.clickBehavior,
            menuItemsCount: this.menuItems.length,
          });
        } catch (err) {
          debugError('TaskCopyLinkButton: Failed to fetch isolation info', err);
        } finally {
          this.loading = false;
        }
      },

      /**
       * Left-click handler. Single-target case copies directly. Multi-target
       * case opens the inline v-menu (already wired via menuAttrs/menuOn).
       */
      async handleClick() {
        await this.ensureDataFetched();
        const behavior = this.clickBehavior;

        debugLog('TaskCopyLinkButton', 'handleClick', {
          behavior,
          taskId: this.task?.id,
        });

        if (behavior === 'direct-copy') {
          const target = getCopyLinkDirectTarget(this.isolationInfo, this.currentAssetId);
          if (!target) {
            debugError('TaskCopyLinkButton', 'no direct target — current facility unknown');
            return;
          }
          await this.copyForFacility({
            facilityId: target.facilityId,
            intent: target.intent,
            facilityName: this.facilityDict[target.facilityId] || target.facilityId,
          });
        } else if (behavior === 'navigate-menu') {
          this.showFacilityMenu = true;
        }
        // 'none' → no-op; button shouldn't have rendered.
      },

      /**
       * Right-click — always opens the menu when there's anything to choose
       * from (i.e., menuItems.length > 0). Single-target cases still get a
       * one-item menu so the user can confirm the destination.
       */
      async handleRightClick() {
        await this.ensureDataFetched();
        debugLog('TaskCopyLinkButton', 'handleRightClick', {
          menuItemsCount: this.menuItems.length,
          taskId: this.task?.id,
        });
        if (this.menuItems.length > 0) {
          this.showFacilityMenu = true;
        }
      },

      /**
       * Copies the chosen facility's URL via the shared helper, picking the
       * right intent encoder via the universal rule (already pre-resolved
       * into `item.intent` by getCopyLinkMenuItems).
       */
      async copyForFacility(item) {
        const taskTypeId = this.effectiveTaskTypeId;
        if (!taskTypeId) {
          this.$dialog?.notify?.error?.(
            'Cannot copy link — task type is not available in this context',
          );
          return;
        }

        debugLog('TaskCopyLinkButton', 'copyForFacility', {
          facilityId: item.facilityId,
          intent: item.intent,
          taskId: this.task?.id,
        });

        const ok = await copyTaskLinkToClipboard({
          facilityId: item.facilityId,
          taskId: this.task.id,
          taskTypeId,
          intent: item.intent,
          dialog: this.$dialog,
        });

        if (ok) {
          this.$emit('copied', { facilityId: item.facilityId, intent: item.intent });
        }
      },

      /**
       * Human-readable subtitle for each menu item, indicating the
       * destination experience.
       */
      intentLabel(intent) {
        if (intent === 'task-board') return 'Open in task board';
        return 'Open in 3D / 360 viewer';
      },
    },

    watch: {
      'task.id': {
        handler() {
          this.dataFetched = false;
          this.isolationInfo = null;
          this.ensureDataFetched();
        },
      },
      'task.forgeTagsIds': {
        handler() {
          this.dataFetched = false;
          this.isolationInfo = null;
          this.ensureDataFetched();
        },
        deep: true,
      },
    },
  };
</script>

<style lang="scss" scoped>
  // ═══════════════════════════════════════════════════════════
  // TASK COPY-LINK BUTTON — Mirrors TaskIsolateButton styling.
  // Uses mdi-link-variant for the button icon; menu items carry
  // intent-aware icons (cube / focus / view-dashboard).
  // ═══════════════════════════════════════════════════════════

  .task-copy-link-button {
    display: inline-flex;
    align-items: center;
  }

  // ─── Loading skeleton (link-chain outline + breathing pulse) ───
  // Same animation primitive as TaskIsolateButton (synchronized opacity
  // pulse on stroke), tuned for the link-chain glyph.
  .task-copy-link-button__skeleton {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
  }

  .task-copy-link-button__skeleton-svg {
    width: 22px;
    height: 22px;
    overflow: visible;
  }

  .task-copy-link-button__skeleton-edge {
    stroke: var(--v-primary-base);
    animation: task-copy-link-breathe 1.6s ease-in-out infinite;
  }

  @keyframes task-copy-link-breathe {
    0%,
    100% {
      opacity: 0.25;
    }
    50% {
      opacity: 1;
    }
  }

  // Accessibility: pin to a midpoint when the user prefers reduced motion.
  @media (prefers-reduced-motion: reduce) {
    .task-copy-link-button__skeleton-edge {
      animation: none;
      opacity: 0.6;
    }
  }

  // ─── Orange Button for Cross-Facility Copy (mirrors isolate's coloring) ───
  .copy-link-btn--cross-facility {
    position: relative;

    ::v-deep .v-icon {
      color: #d97706 !important;
      transition: all 0.2s ease;
    }

    &:hover ::v-deep .v-icon {
      color: #b45309 !important;
      transform: scale(1.1);
    }
  }

  // ─── Copy-Link Menu (mirrors facility-menu, with subtitle support) ───
  .copy-link-menu {
    min-width: 240px;
    max-width: 360px;
    border-radius: 8px !important;
    overflow: hidden;
    border: 1px solid rgba(217, 119, 6, 0.2);

    &__list {
      padding: 0 !important;
    }

    &__header {
      background: linear-gradient(135deg, rgba(217, 119, 6, 0.08) 0%, rgba(217, 119, 6, 0.04) 100%);
      font-weight: 600;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #d97706 !important;
      padding: 12px 16px !important;
      height: auto !important;
    }

    &__item {
      transition: all 0.15s ease;
      min-height: 48px;

      &:hover {
        background: rgba(217, 119, 6, 0.08) !important;
      }

      &:active {
        background: rgba(217, 119, 6, 0.15) !important;
      }
    }

    &__name {
      font-weight: 500;
      font-size: 13px;
      color: inherit;
    }

    &__intent {
      font-size: 11px;
      opacity: 0.7;
    }
  }

  // ─── Theme-aware adjustments ───
  .theme--dark {
    .copy-link-menu {
      border-color: rgba(217, 119, 6, 0.3);

      &__header {
        background: linear-gradient(
          135deg,
          rgba(217, 119, 6, 0.15) 0%,
          rgba(217, 119, 6, 0.08) 100%
        );
      }

      &__item:hover {
        background: rgba(217, 119, 6, 0.12) !important;
      }
    }
  }
</style>
